import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./AppPage.module.css";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedApp, resetApp } from "store/app/appSlice";
import { List, Paper, Stack, Alert, Card, CardContent } from "@mui/material";
import AppItem from "./AppItem";
import AppDetails from "./AppDetails";
import { UploadButton } from "components/buttons";
import { CircularProgress } from "components/overlays";
import { HidingError } from "components/errors";
import { useGetAppsQuery, useAddAppVersionMutation, useEditAppMutation, useRemoveAppMutation } from "services/data/appApi";
import { getManagerSearch } from "store/searchbar/selectors";
import { useQueryStale } from "rtkq/useQueryStale";

const DEFAULT_ERROR = "Ошибка соединения с сервером";
const AppPage = ({setActiveToolbar, defaultSearch}) => {

    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const { selectedApp } = useSelector(state => state.app);
    
    const queryArgs = useSelector((state) => getManagerSearch(state.searchbar, 'app', defaultSearch.app));
        
    const appQuery = useGetAppsQuery(queryArgs);
    const { data, isError,  isFetching } = appQuery;
    useQueryStale({query: appQuery, endpointName: 'getApps', queryArgs: queryArgs});


    const [removeApp, {error: removeAppError, isLoading: isRemovingApp, reset: removeAppReset}] = useRemoveAppMutation();
    const [editApp, {error: editAppError, isLoading: isEditingApp, reset: editAppReset}] = useEditAppMutation();

    
    const [addAppVersion, {error: addAppVersionError, isLoading: isAddingAppVersion, reset: addAppVersionReset}] = useAddAppVersionMutation();                                                                    
    const itemsRef = useRef([]);

    useEffect(() => setActiveToolbar("app"), []);
    useEffect(() => setLoading(isAddingAppVersion), [isAddingAppVersion]);

    useEffect(()=> {
      itemsRef.current = itemsRef.current.slice(0, data?.body?.items?.length);
    }, [data]);

    if(isFetching) return <div>Загрузка...</div>;
    if(isError) return <div>Ошибка!</div>;

    const onItemSelect = (item) => dispatch(setSelectedApp(item));    
    const isItemSelected = (item) => item?.id===selectedApp?.id;


    const hideError = () => {
      if(addAppVersionError) addAppVersionReset();
      if(removeAppError) removeAppReset();
      if(editAppError) editAppReset();
    };
    
    const openError = () => { 
      return !!addAppVersionError || !!removeAppError || !!editAppError || false;
    };

    const getErrorMessage = () => {
      if(addAppVersionError) return addAppVersionError?.data?.message ?? DEFAULT_ERROR;
      if(removeAppError) return removeAppError?.data?.message ?? DEFAULT_ERROR;
      if(editAppError) return editAppError?.data?.message ?? DEFAULT_ERROR;
      else return'Ошибка';
    };

    const saveDetails = (packageName, options, details) => {
      if(details?.length) {
        editApp({packageName: packageName, currentQueryArgs: queryArgs, body:{...options, items: details}})
          .unwrap()
          .then((payload) => {
            if(payload?.body?.items?.length === 0) {
              dispatch(resetApp());
            }
          })
          .catch((error) => console.log(error)
        );
      }
      else {
        removeApp({packageName: packageName, currentQueryArgs: queryArgs})
          .unwrap()
          .then((payload) => {
            dispatch(resetApp());
          })
          .catch((error) => console.log(error)
        );
      }
    };

    const uploadAppVersion = (e, clear) => { 
        if(e?.target?.files.length) {
            const formData = new FormData();
            formData.append('file', e.target.files[0]); 
            
            addAppVersion(formData).unwrap()
              .then((payload) => {
                  const appPackage = payload.body.package;
                  const item = data.body.items.find(el => el.package === appPackage);
                  if(item) {
                    dispatch(setSelectedApp({id: item.id, name: item.name, packageName: item.package}));
                    scrollTo(item);
                  }
                  
              })
              .catch( error => console.log(error))
              .finally(() => {e.target.value = null});
        }
    };

    const scrollTo = (item) => {
      const element = itemsRef.current.find(el => el.id === item.id);
      element && element.el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    const renderItems = data.body.items.map((item, index) => {
      return  <AppItem
                  key={item.id}
                  ref={el => itemsRef.current[index] = {id: item.id, el: el}}
                  selected={isItemSelected(item)}
                  onSelect={onItemSelect}
                  { ...item} 
              />;             
    });

    const renderDetails = <AppDetails
                            item={selectedApp}
                            saveDetails={saveDetails}
                          />;
    

    const renderEmpty =   <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
                            <CardContent sx={{overflow: 'auto'}}>
                                <div>{data ? `Всего приложений в магазине: ${data.body.total}` : "Приложений пока нет" }</div>
                            </CardContent>
                          </Card>;

    return (    
        <div className="page">
          <HidingError 
            open={ openError() }
            message={ getErrorMessage() }
            onClose={hideError}
          />             
          <div className={styles.overlayWrapper}>
            <CircularProgress open={loading}/>
            <Paper elevation={1} sx={{flexShrink: 0, marginBottom: '4px'}}>
              <Stack direction="row" spacing={1} className="toolbar">
                <Alert 
                  icon={false}
                  severity="info"
                  sx={[
                    {width: "100%", justifyContent: "center", alignItems: "center", padding: '0'},
                    {'.MuiAlert-message': {padding: '6px 0'}}
                  ]}
                >
                  <UploadButton 
                    tip="Загрузить приложение в кабинет"
                    text="Добавить"
                    color="info"
                    handler={uploadAppVersion} 
                  />
                </Alert>
              </Stack>
            </Paper>
            <div className="page-content">
              <div className="left">
                <div className="pagelist">
                  <div className="pagelist-body">
                    <List >{renderItems}</List>
                  </div>
                </div>
              </div>
              <div className="right">
                <div className={styles.details}>  
                  <div className={styles.content}>
                    {
                      selectedApp ? renderDetails : renderEmpty
                    }
                  </div> 
                </div>   
              </div>
            </div>
          </div>
        </div>
    );
};

export default AppPage;